import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, MessageCircle, Sparkles } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { AvailabilityBar } from "../components/AvailabilityBar";
import { RoomCard } from "../components/RoomCard";
import { Testimonials } from "../components/Testimonials";
import { useSite } from "../context/SiteContext";
import { GALLERY_PHOTOS, OFFERS, track, whatsappLink } from "../lib/site";

const BENTO = [
  { to: "/spa", image: GALLERY_PHOTOS[2], label: "Restore", title: "The Sutra Spa", cls: "cell-a", id: "spa" },
  { to: "/experiences", image: GALLERY_PHOTOS[3], label: "Drift", title: "Infinity Pool", cls: "cell-b", id: "pool" },
  { to: "/experiences", image: GALLERY_PHOTOS[4], label: "Wander", title: "Nature Trails", cls: "cell-c", id: "trails" },
  { to: "/dining", image: GALLERY_PHOTOS[0], label: "Feast", title: "Garden Dining", cls: "cell-d", id: "dining" },
  { to: "/offers", image: GALLERY_PHOTOS[1], label: "Celebrate", title: "Honeymoons", cls: "cell-e", id: "honeymoons" },
  { to: "/contact", image: GALLERY_PHOTOS[3], label: "Gather", title: "Weddings & Events", cls: "cell-f", id: "weddings" },
];

function HeroSlider({ photos }) {
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setSlide((s) => (s + 1) % photos.length), 6000);
    return () => clearInterval(timer);
  }, [photos.length]);
  const go = (next) => setSlide((s) => (s + photos.length + next) % photos.length);
  return (
    <>
      <AnimatePresence mode="sync">
        <motion.div
          key={slide}
          className="hero-slide"
          style={{ backgroundImage: `url('${photos[slide]}')` }}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`hero-slide-${slide}`}
        />
      </AnimatePresence>
      <div className="hero-slider-controls">
        <button className="hero-arrow" onClick={() => go(-1)} aria-label="Previous photo" data-testid="hero-prev"><ChevronLeft size={18} /></button>
        <div className="hero-dots" data-testid="hero-dots">
          {photos.map((_, i) => (
            <button key={i} className={i === slide ? "hero-dot active" : "hero-dot"} onClick={() => setSlide(i)} aria-label={`Photo ${i + 1}`} data-testid={`hero-dot-${i}`} />
          ))}
        </div>
        <button className="hero-arrow" onClick={() => go(1)} aria-label="Next photo" data-testid="hero-next"><ChevronRight size={18} /></button>
      </div>
    </>
  );
}

export default function Home() {
  const { rooms, settings, gallery, openBooking } = useSite();
  const featured = gallery.filter((p) => p.featured).map((p) => p.image);
  const sliderPhotos = featured.length ? featured : gallery.length ? gallery.map((p) => p.image) : GALLERY_PHOTOS;
  return (
    <main>
      <section className="hero" data-testid="hero-section">
        <HeroSlider photos={sliderPhotos} />
        <div className="hero-overlay" />
        <div className="hero-content">
          <motion.span className="eyebrow light" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
            Anand · Gujarat
          </motion.span>
          <MaskedLines lines={[<>{settings.hero_line1 || "Escape the ordinary."}</>, <em>{settings.hero_line2 || "Arrive extraordinary."}</em>]} className="hero-title" />
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            {settings.hero_subtitle || "Anand's most-loved luxury escape — eight stunning stays, a soulful spa, garden dining and evenings you'll never forget."}
          </motion.p>
          <motion.div className="hero-buttons" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}>
            <button className="gold-button" onClick={() => { track("book_now_click", { location: "hero" }); openBooking({}); }} data-testid="hero-book-button">
              Book your escape <ArrowUpRight size={16} />
            </button>
            <a
              href={whatsappLink("Hello Madhubhan Resort & Spa, I would like to enquire about a stay.", settings.whatsapp)}
              target="_blank" rel="noreferrer" className="text-link light-link"
              onClick={() => track("whatsapp_click", { location: "hero" })}
              data-testid="hero-whatsapp-link"
            >
              <MessageCircle size={16} /> WhatsApp reservations
            </a>
          </motion.div>
        </div>
        <motion.div className="hero-note" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }}>
          <span>01</span>
          <div><strong>THE SLOW LUXURY OF</strong><br />Madhubhan</div>
        </motion.div>
      </section>

      <AvailabilityBar />

      <Marquee />

      <section className="intro intro-bg section-pad" style={{ backgroundImage: `url('${GALLERY_PHOTOS[1]}')` }} data-testid="intro-section">
        <Reveal className="section-kicker">A softer pace</Reveal>
        <div className="intro-copy">
          <Reveal><h2>Slow mornings.<br /><em>Golden evenings.</em></h2></Reveal>
          <Reveal delay={0.12}>
            <p>Wake to birdsong, breakfast under the trees and long afternoons by the infinity pool. At Madhubhan, every detail — from the first chai to the last candlelight — is designed to make you stay a little longer.</p>
            <Link to="/about" className="text-link" data-testid="intro-about-link">Our story <ArrowUpRight size={15} /></Link>
          </Reveal>
        </div>
        <Reveal className="intro-stats" delay={0.15}>
          <div><strong>8</strong><span>Rooms, cottages<br />& suites</span></div>
          <div><strong>2 PM</strong><span>Check-in<br />24-hour arrival desk</span></div>
          <div><strong>72h</strong><span>Free cancellation<br />before arrival</span></div>
          <div><strong>24/7</strong><span>Concierge &<br />guest care</span></div>
        </Reveal>
      </section>

      <section className="stay-section section-pad" data-testid="featured-rooms-section">
        <div className="section-heading">
          <Reveal><div><span className="eyebrow">01 — The sanctuary</span><h2>Eight stays.<br /><em>Zero compromises.</em></h2></div></Reveal>
          <Reveal delay={0.1}><p>Hand-picked rooms, village cottages and signature suites — each with breakfast for two, taxes included and a Book Now that actually books. Your dates are always open here.</p></Reveal>
        </div>
        <div className="room-grid">
          {rooms.map((room, i) => <RoomCard room={room} index={i} key={room.id} />)}
        </div>
        <Reveal className="catalogue-note">
          <Sparkles size={18} />
          <span><strong>All eight stays are open for your dates.</strong> Filter by rooms, cottages or suites on the full catalogue page.</span>
          <Link to="/rooms" data-testid="featured-rooms-all-link">Compare all stays <ArrowUpRight size={15} /></Link>
        </Reveal>
      </section>

      <section className="bento-section section-pad" data-testid="experience-bento">
        <div className="section-heading">
          <Reveal><div><span className="eyebrow">02 — The experience</span><h2>Moments you'll<br /><em>replay forever.</em></h2></div></Reveal>
          <Reveal delay={0.1}><p>Spa rituals, poolside sunsets, garden feasts and celebrations under the stars — this is what a Madhubhan day feels like.</p></Reveal>
        </div>
        <div className="bento">
          {BENTO.map((cell, i) => (
            <motion.div
              key={cell.id}
              className={`bento-cell ${cell.cls}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`bento-${cell.id}`}
            >
              <Link to={cell.to}>
                <img src={cell.image} alt={cell.title} loading="lazy" />
                <div className="bento-label"><span>{cell.label}</span><h3>{cell.title}</h3></div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="offers-teaser section-pad" data-testid="offers-teaser">
        <div className="section-heading">
          <Reveal><div><span className="eyebrow light">03 — Seasonal packages</span><h2>Packages worth<br /><em>packing for.</em></h2></div></Reveal>
          <Reveal delay={0.1}><p>Candlelit honeymoons, laughter-filled family weekends, early-bird savings and spontaneous escapes — four packages, endless memories.</p></Reveal>
        </div>
        <div className="offer-strip">
          {OFFERS.slice(0, 2).map((offer, i) => (
            <Reveal key={offer.id} delay={i * 0.1} className="offer-teaser-card">
              <img src={offer.image} alt={offer.name} loading="lazy" />
              <div>
                <span className="eyebrow light">Package</span>
                <h3>{offer.name}</h3>
                <p>{offer.tagline}</p>
                <button className="text-link light-link as-button" onClick={() => { track("offer_enquiry", { offer: offer.name }); openBooking({ offer }); }} data-testid={`offer-teaser-book-${offer.id}`}>
                  Reserve this offer <ArrowUpRight size={15} />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="center-row"><Link to="/offers" className="gold-button" data-testid="offers-teaser-all-link">See all four offers <ArrowUpRight size={16} /></Link></Reveal>
      </section>

      <Testimonials />
    </main>
  );
}
